import { useCallback, useEffect, useMemo, useRef, useState, type FC } from 'react'
import { Autocomplete, Box, Button, CircularProgress, Stack, Typography, useMediaQuery, useTheme } from '@mui/material'
import { useNavigate } from 'react-router-dom'
import { ButtonActionBar } from 'components/ButtonActionBar'
import { FileTextOutlined, ThunderboltOutlined } from '@ant-design/icons'
import { AnimatePresence, motion } from 'framer-motion'
import { useGetMenuMaster } from 'api/menu'
import { DRAWER_WIDTH, MINI_DRAWER_WIDTH } from 'config'
import useAuth from 'hooks/useAuth'
import useConfig from 'hooks/useConfig'
import { usePeriods } from 'hooks/usePeriods'
import { useGlobalState } from 'contexts/GlobalStateContext'
import { useSSE, type SSEEvent } from 'contexts/SSEContext'
import { createAgentSession, fileToBase64, getFileTypeExtension, sendAgentMessage } from 'services/agentSessionsService'
import { getRootOrganizationId } from 'types/user-profile'
import {
  useBulkCreateRewards,
  useBulkCreateRewardCategories,
  invalidateRewardsCache,
  useFetchRewards,
} from 'hooks/useRewards'
import { useCreateRewardPackage, useAssignRewardPackage, invalidateRewardPackagesCache } from 'hooks/useRewardPackages'
import { useSaveUserRewards } from 'hooks/useUserRewards'
import SimpleTitle from 'components/SimpleTitle'
import SinglePeriodSelector from 'components/SinglePeriodSelector'
import { TextField } from 'components/@extended/TextField'
import {
  ImportConversation,
  ThinkingIndicator,
  ReviewTable,
  SmartImportDropZone,
  SmartImportEmployeeMatchTable,
  SmartImportEmployeeRewardsSummary,
  SmartImportOptions,
  SmartImportProcessing,
  SmartImportFileInfo,
  SmartImportSummary,
  SmartImportChatInput,
  SmartImportNoChanges,
  SmartImportPackageForm,
} from './components'
import type {
  AgentMessage,
  CategoryData,
  EmployeeMatchUserOption,
  EmployeeSourceRowMatchResult,
  ImportOption,
  RewardData,
} from './types'
import type { CreateRewardCategoryDTO, CreateRewardDTO, Reward, UserRewardItem } from 'types/rewards'
import { MenuOrientation } from 'types/config'
import { findPeriodById } from 'types/periods'

type SmartImportPhase =
  | 'welcome'
  | 'select-type'
  | 'select-period'
  | 'processing'
  | 'review-categories'
  | 'review-rewards'
  | 'review-package'
  | 'review-employee-matches'

type SmartImportOptionId = ImportOption['id'] | 'employee-reward-package'

interface SmartImportOption extends Omit<ImportOption, 'id'> {
  id: SmartImportOptionId
}

interface PackageRewardItemState {
  rewardId: string
  rewardName: string
  categoryName: string
  categoryDisplayOrder: number
  rewardDisplayOrder: number
  selected: boolean
  companyContribution: number
  employeeContribution: number
  supportsEmployeeContribution: boolean
}

interface ExistingPackageOption {
  id: string
  name: string
  rewards: Array<{
    rewardId: string
    displayOrder: number
    companyContribution: number
    employeeContribution: number
  }>
}

interface ParsedSpreadsheetContribution {
  company?: number
  employee?: number
  single?: number
}

const rewardValueIgnoredColumns = new Set([
  'first name',
  'last name',
  'email',
  'team',
  'title',
  'employee id',
  'employee_id',
  'full name',
  'full_name',
  'name',
])

const normalizeRewardLabel = (value: string): string =>
  value
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, ' ')
    .trim()

const parseNumberValue = (value: unknown): number | null => {
  if (typeof value === 'number' && Number.isFinite(value)) {
    return value
  }
  if (typeof value !== 'string') {
    return null
  }

  const trimmed = value.trim()
  if (!trimmed) {
    return null
  }

  const normalizedNegative = trimmed.replace(/^\((.*)\)$/, '-$1')
  const cleaned = normalizedNegative.replace(/[$,%\s,]/g, '')
  if (!cleaned) {
    return null
  }

  const parsed = Number(cleaned)
  return Number.isFinite(parsed) ? parsed : null
}

const parseSpreadsheetContributionValues = (
  rawValues?: Record<string, string | number | boolean | null>
): Map<string, ParsedSpreadsheetContribution> => {
  const parsed = new Map<string, ParsedSpreadsheetContribution>()
  if (!rawValues) {
    return parsed
  }

  Object.entries(rawValues).forEach(([rawHeader, rawValue]) => {
    const header = rawHeader.trim()
    if (!header) return

    const normalizedHeader = normalizeRewardLabel(header)
    if (rewardValueIgnoredColumns.has(normalizedHeader)) {
      return
    }

    const numericValue = parseNumberValue(rawValue)
    if (numericValue === null) {
      return
    }

    const companyMatch = /\s*\(company\)$/i.exec(header)
    const employeeMatch = /\s*\(employee\)$/i.exec(header)

    const rewardLabel = normalizeRewardLabel(
      companyMatch || employeeMatch ? header.replace(/\s*\((company|employee)\)$/i, '') : header
    )
    if (!rewardLabel) {
      return
    }

    const existing = parsed.get(rewardLabel) || {}
    if (companyMatch) {
      existing.company = numericValue
    } else if (employeeMatch) {
      existing.employee = numericValue
    } else {
      existing.single = numericValue
    }
    parsed.set(rewardLabel, existing)
  })

  return parsed
}

const uuidPattern = /[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}/g
const strictUUIDPattern = /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/

const extractPackageDecision = (
  content: string
): { matchedPackageIds: string[]; recommendedPackageId: string | null } => {
  const matchedPackageIds = new Set<string>()

  const matchedLine = content.match(/MATCHED_PACKAGE_IDS?:\s*\[([^\]]*)\]/i)
  if (matchedLine?.[1]) {
    const ids = matchedLine[1].match(uuidPattern) || []
    ids.forEach((id) => matchedPackageIds.add(id))
  }

  const packageScopedMatches = content.matchAll(
    /package[^\n]*?(?:ID\s*:\s*`?([0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12})`?)/gi
  )
  for (const match of packageScopedMatches) {
    if (match[1]) {
      matchedPackageIds.add(match[1])
    }
  }

  const explicitRecommendation = content.match(
    /RECOMMENDED_PACKAGE_ID:\s*`?(none|[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12})`?/i
  )

  let recommendedPackageId: string | null = null
  if (explicitRecommendation?.[1] && explicitRecommendation[1].toLowerCase() !== 'none') {
    recommendedPackageId = explicitRecommendation[1]
  }

  if (!recommendedPackageId) {
    const fallbackMatch = content.match(
      /\bID:\s*`?([0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12})`?/
    )
    recommendedPackageId = fallbackMatch?.[1] || null
  }

  if (recommendedPackageId) {
    matchedPackageIds.add(recommendedPackageId)
  }

  return {
    matchedPackageIds: Array.from(matchedPackageIds),
    recommendedPackageId,
  }
}

const syntheticOrdinalCategoryIDPattern = /^00000000-0000-0000-0000-([0-9a-fA-F]{12})$/
const syntheticTokenCategoryIDPattern = /^cat-(.+)-(\d+)$/i

const isUUID = (value: string): boolean => strictUUIDPattern.test(value.trim())

const normalizeAlphaNum = (value: string): string => value.toLowerCase().replace(/[^a-z0-9]/g, '')

const resolveSyntheticCategoryByOrder = (
  rawCategoryId: string,
  categories: CategoryData[]
): CategoryData | undefined => {
  const trimmed = rawCategoryId.trim()
  const tokenMatch = syntheticTokenCategoryIDPattern.exec(trimmed)
  const tokenLabel = tokenMatch?.[1] || ''
  const tokenOrdinal = tokenMatch?.[2] ? parseInt(tokenMatch[2], 10) : NaN

  if (tokenLabel) {
    const normalizedTokenLabel = normalizeAlphaNum(tokenLabel)
    if (normalizedTokenLabel) {
      const byLabel = categories.find((category) => normalizeAlphaNum(category.name) === normalizedTokenLabel)
      if (byLabel) {
        return byLabel
      }
    }
  }

  const uuidMatch = syntheticOrdinalCategoryIDPattern.exec(trimmed)
  const uuidOrdinal = uuidMatch?.[1] ? parseInt(uuidMatch[1], 16) : NaN
  const ordinal =
    Number.isFinite(tokenOrdinal) && tokenOrdinal > 0
      ? tokenOrdinal
      : Number.isFinite(uuidOrdinal) && uuidOrdinal > 0
        ? uuidOrdinal
        : NaN
  if (!Number.isFinite(ordinal) || ordinal <= 0) return undefined

  const orderedCategories = [...categories].sort((a, b) => {
    if (a.displayOrder !== b.displayOrder) return a.displayOrder - b.displayOrder
    return a.name.localeCompare(b.name)
  })

  const byDisplayOrder = orderedCategories.find((category) => category.displayOrder === ordinal)
  if (byDisplayOrder) return byDisplayOrder

  return orderedCategories[ordinal - 1]
}

const resolveCategoryReference = (
  rawCategoryId: string,
  explicitCategoryName: string | undefined,
  categoriesById: Map<string, CategoryData>,
  categoriesByName: Map<string, CategoryData>,
  categories: CategoryData[]
): CategoryData | undefined => {
  const trimmedId = rawCategoryId.trim()
  if (trimmedId) {
    const byId = categoriesById.get(trimmedId)
    if (byId) return byId

    const byNameFromId = categoriesByName.get(trimmedId.toLowerCase())
    if (byNameFromId) return byNameFromId
  }

  const trimmedName = (explicitCategoryName || '').trim()
  if (trimmedName) {
    const byName = categoriesByName.get(trimmedName.toLowerCase())
    if (byName) return byName
  }

  if (trimmedId) {
    return resolveSyntheticCategoryByOrder(trimmedId, categories)
  }

  return undefined
}

const SmartImport: FC = () => {
  const theme = useTheme()
  const navigate = useNavigate()
  const { menuMaster } = useGetMenuMaster()
  const { menuOrientation } = useConfig()
  const downLG = useMediaQuery(theme.breakpoints.down('lg'))
  const isHorizontal = menuOrientation === MenuOrientation.HORIZONTAL && !downLG
  const drawerOffset = isHorizontal ? 0 : menuMaster?.isDashboardDrawerOpened ? DRAWER_WIDTH : MINI_DRAWER_WIDTH

  // Auth and context
  const { token, profile } = useAuth()
  const { purcentAppState } = useGlobalState()
  const { subscribe } = useSSE()

  const { bulkCreateCategories, isLoading: isCreatingCategories } = useBulkCreateRewardCategories(token || '')
  const { bulkCreateRewards, isLoading: isCreatingRewards } = useBulkCreateRewards(token || '')
  const { createPackage, isLoading: isCreatingPackage } = useCreateRewardPackage(token || '')
  const { assignPackage } = useAssignRewardPackage(token || '')
  const { saveUserRewards } = useSaveUserRewards(token || '')

  // State
  const [phase, setPhase] = useState<SmartImportPhase>('welcome')
  const [selectedOption, setSelectedOption] = useState<SmartImportOptionId | null>(null)
  const [uploadedFile, setUploadedFile] = useState<File | null>(null)
  const [isDragging, setIsDragging] = useState(false)
  const [processingStatus, setProcessingStatus] = useState<string | null>(null)
  const [isUploadingFile, setIsUploadingFile] = useState(false)
  const [categories, setCategories] = useState<CategoryData[]>([])
  const [rewards, setRewards] = useState<RewardData[]>([])
  const [approvedCategories, setApprovedCategories] = useState<CategoryData[] | null>(null)
  const [approvedRewards, setApprovedRewards] = useState<RewardData[] | null>(null)
  const [messages, setMessages] = useState<AgentMessage[]>([])
  const [chatMessage, setChatMessage] = useState('')
  const [sessionId, setSessionId] = useState<string | null>(null)
  const [activeAgents, setActiveAgents] = useState<Set<string>>(new Set())
  const [packageName, setPackageName] = useState('')
  const [packageDescription, setPackageDescription] = useState('')
  const [packageDisclaimer, setPackageDisclaimer] = useState('')
  const [packageError, setPackageError] = useState<string | null>(null)
  const [createdPackageId, setCreatedPackageId] = useState<string | null>(null)
  const [existingPackageOptions, setExistingPackageOptions] = useState<ExistingPackageOption[]>([])
  const [selectedExistingPackageId, setSelectedExistingPackageId] = useState('')
  const [recommendedExistingPackageId, setRecommendedExistingPackageId] = useState<string | null>(null)
  const [matchedPackageCandidateIds, setMatchedPackageCandidateIds] = useState<string[]>([])
  const [packageStep, setPackageStep] = useState(0)
  const [packageRewardItems, setPackageRewardItems] = useState<Map<string, PackageRewardItemState>>(new Map())
  const [isPackageInitialized, setIsPackageInitialized] = useState(false)
  const [selectedPeriodId, setSelectedPeriodId] = useState('')
  const [selectedRootPeriodId, setSelectedRootPeriodId] = useState('')
  const [employeeMatches, setEmployeeMatches] = useState<EmployeeSourceRowMatchResult[]>([])
  const [employeeMatchUserOptions, setEmployeeMatchUserOptions] = useState<EmployeeMatchUserOption[]>([])
  const [employeeMatchError, setEmployeeMatchError] = useState<string | null>(null)
  const [employeeRewardSummary, setEmployeeRewardSummary] = useState<{
    total: number
    successful: number
    failed: number
    failedUserIds: string[]
  } | null>(null)
  const [isCreatingEmployeeRewards, setIsCreatingEmployeeRewards] = useState(false)
  const [hasRetriedEmptyEmployeeImport, setHasRetriedEmptyEmployeeImport] = useState(false)
  const [hasRetriedNoPackageDraftFlow, setHasRetriedNoPackageDraftFlow] = useState(false)
  const pendingPhaseRef = useRef<SmartImportPhase | null>(null)
  const latestCategoriesRef = useRef<CategoryData[]>([])
  const latestApprovedCategoriesRef = useRef<CategoryData[] | null>(null)
  const [isSavingCategories, setIsSavingCategories] = useState(false)
  const [isSavingRewards, setIsSavingRewards] = useState(false)
  const isSavingCategoryChanges = isSavingCategories || isCreatingCategories
  const isSavingRewardChanges = isSavingRewards || isCreatingRewards
  const scrollAnchorRef = useRef<HTMLDivElement>(null)
  const pageScrollAnchorRef = useRef<HTMLDivElement>(null)
  const rootOrganizationId = useMemo(() => {
    if (!profile) return ''
    return getRootOrganizationId(profile) || ''
  }, [profile])
  const isRewardPackageFlow = selectedOption === 'reward-package' || selectedOption === 'employee-reward-package'
  const { periods: periodsResponse, isLoadingPeriods } = usePeriods(
    token || '',
    rootOrganizationId || null,
    !!token && !!rootOrganizationId && (selectedOption === 'employee-reward-package' || phase === 'select-period')
  )
  const packageSourceRewards = approvedRewards ?? rewards
  const packageSourceCategories = approvedCategories ?? categories
  const shouldFetchPackageRewards =
    !!token &&
    !!rootOrganizationId &&
    isRewardPackageFlow &&
    (packageSourceRewards.length > 0 || selectedOption === 'employee-reward-package')
  const { rewards: rewardsCatalog } = useFetchRewards(rootOrganizationId, token || '', shouldFetchPackageRewards)
  const categoryNameToId = useMemo(() => {
    const map = new Map<string, string>()
    packageSourceCategories.forEach((category) => {
      if (category.id) {
        map.set(category.name.toLowerCase(), category.id)
      }
    })
    return map
  }, [packageSourceCategories])
  const categoryById = useMemo(() => {
    const map = new Map<string, CategoryData>()
    packageSourceCategories.forEach((category) => {
      if (category.id) {
        map.set(category.id, category)
      }
    })
    return map
  }, [packageSourceCategories])

  const resolvedPackageRewards = useMemo(() => {
    if (packageSourceRewards.length === 0) return []
    const rewardLookup = new Map<string, string>()
    ;(rewardsCatalog ?? []).forEach((reward) => {
      if (reward.id) {
        rewardLookup.set(`${reward.name.toLowerCase()}|${reward.categoryId}`, reward.id)
      }
    })

    return packageSourceRewards.map((reward) => {
      const rawCategoryId = reward.categoryId || ''
      const categoryKey = rawCategoryId.toLowerCase()
      const resolvedCategoryId =
        categoryNameToId.get(categoryKey) ||
        (reward.categoryName ? categoryNameToId.get(reward.categoryName.toLowerCase()) : undefined) ||
        rawCategoryId
      const lookupKey = `${reward.name.toLowerCase()}|${resolvedCategoryId}`
      const resolvedRewardId = reward.id || rewardLookup.get(lookupKey) || ''
      return {
        ...reward,
        resolvedCategoryId,
        resolvedRewardId,
      }
    })
  }, [packageSourceRewards, rewardsCatalog, categoryNameToId])

  useEffect(() => {
    if (phase !== 'review-package') return
    if (isPackageInitialized) return
    if (resolvedPackageRewards.length === 0) return

    const items = new Map<string, PackageRewardItemState>()
    resolvedPackageRewards.forEach((reward) => {
      if (!reward.resolvedRewardId) return
      const category = reward.resolvedCategoryId ? categoryById.get(reward.resolvedCategoryId) : undefined
      const categoryName = reward.categoryName || category?.name || reward.categoryId || 'Uncategorized'
      const categoryDisplayOrder = category?.displayOrder ?? 0
      items.set(reward.resolvedRewardId, {
        rewardId: reward.resolvedRewardId,
        rewardName: reward.name,
        categoryName,
        categoryDisplayOrder,
        rewardDisplayOrder: reward.displayOrder ?? 0,
        selected: true,
        companyContribution: 0,
        employeeContribution: 0,
        supportsEmployeeContribution: reward.supportsEmployeeContribution,
      })
    })

    if (items.size > 0) {
      setPackageRewardItems(items)
      setIsPackageInitialized(true)
    }
  }, [categoryById, isPackageInitialized, phase, resolvedPackageRewards])

  const missingPackageRewardsCount = resolvedPackageRewards.filter((reward) => !reward.resolvedRewardId).length
  const packageSelectedRewards = useMemo(() => {
    const selected: PackageRewardItemState[] = []
    packageRewardItems.forEach((item) => {
      if (item.selected) {
        selected.push(item)
      }
    })
    selected.sort((a, b) => {
      const categoryCompare = a.categoryDisplayOrder - b.categoryDisplayOrder
      if (categoryCompare !== 0) return categoryCompare
      return a.rewardDisplayOrder - b.rewardDisplayOrder
    })
    return selected
  }, [packageRewardItems])

  const packageRewardsByCategory = useMemo(() => {
    const categoriesMap = new Map<
      string,
      { categoryName: string; categoryDisplayOrder: number; rewards: PackageRewardItemState[] }
    >()

    packageRewardItems.forEach((item) => {
      if (!categoriesMap.has(item.categoryName)) {
        categoriesMap.set(item.categoryName, {
          categoryName: item.categoryName,
          categoryDisplayOrder: item.categoryDisplayOrder,
          rewards: [],
        })
      }
      categoriesMap.get(item.categoryName)!.rewards.push(item)
    })

    categoriesMap.forEach((category) => {
      category.rewards.sort((a, b) => a.rewardDisplayOrder - b.rewardDisplayOrder)
    })

    return Array.from(categoriesMap.values()).sort((a, b) => a.categoryDisplayOrder - b.categoryDisplayOrder)
  }, [packageRewardItems])

  const packageSelectedRewardsByCategory = useMemo(() => {
    const categoriesMap = new Map<
      string,
      { categoryName: string; categoryDisplayOrder: number; rewards: PackageRewardItemState[] }
    >()

    packageSelectedRewards.forEach((item) => {
      if (!categoriesMap.has(item.categoryName)) {
        categoriesMap.set(item.categoryName, {
          categoryName: item.categoryName,
          categoryDisplayOrder: item.categoryDisplayOrder,
          rewards: [],
        })
      }
      categoriesMap.get(item.categoryName)!.rewards.push(item)
    })

    categoriesMap.forEach((category) => {
      category.rewards.sort((a, b) => a.rewardDisplayOrder - b.rewardDisplayOrder)
    })

    return Array.from(categoriesMap.values()).sort((a, b) => a.categoryDisplayOrder - b.categoryDisplayOrder)
  }, [packageSelectedRewards])

  const packageTotals = useMemo(() => {
    let company = 0
    let employee = 0
    packageSelectedRewards.forEach((item) => {
      company += item.companyContribution
      employee += item.employeeContribution
    })
    return { company, employee, total: company + employee }
  }, [packageSelectedRewards])

  const packageAllSelected =
    packageRewardItems.size > 0 && Array.from(packageRewardItems.values()).every((item) => item.selected)
  const packageSomeSelected =
    Array.from(packageRewardItems.values()).some((item) => item.selected) && !packageAllSelected
  const canCreatePackage =
    packageName.trim().length > 0 && packageSelectedRewards.length > 0 && missingPackageRewardsCount === 0
  const unresolvedEmployeeMatchCount = employeeMatches.filter((row) => row.status === 'unresolved').length
  const skippedEmployeeMatchCount = employeeMatches.filter((row) => row.status === 'skipped').length
  const matchedEmployeeRows = employeeMatches.filter(
    (row) => row.status === 'matched' && !!(row.assignedUserId || row.matchedUser?.id)
  )
  const matchedExistingPackageOptions = useMemo(
    () => existingPackageOptions.filter((pkg) => matchedPackageCandidateIds.includes(pkg.id)),
    [existingPackageOptions, matchedPackageCandidateIds]
  )
  const matchedPackageCount = matchedExistingPackageOptions.length
  const effectivePackageId = createdPackageId || selectedExistingPackageId || null
  const selectedExistingPackageOption =
    matchedExistingPackageOptions.find((pkg) => pkg.id === selectedExistingPackageId) ||
    existingPackageOptions.find((pkg) => pkg.id === selectedExistingPackageId) ||
    null
  const activePackageRewards = useMemo(() => {
    if (createdPackageId) {
      return packageSelectedRewards.map((reward) => ({
        rewardId: reward.rewardId,
        displayOrder: reward.rewardDisplayOrder,
        companyContribution: reward.companyContribution,
        employeeContribution: reward.employeeContribution,
      }))
    }

    if (selectedExistingPackageOption) {
      return selectedExistingPackageOption.rewards
    }

    return [] as ExistingPackageOption['rewards']
  }, [createdPackageId, packageSelectedRewards, selectedExistingPackageOption])
  const rewardsCatalogById = useMemo(() => {
    const map = new Map<string, Reward>()
    ;(rewardsCatalog || []).forEach((reward) => {
      if (reward.id) {
        map.set(reward.id, reward)
      }
    })
    return map
  }, [rewardsCatalog])
  const categoryNameById = useMemo(() => {
    const map = new Map<string, string>()
    packageSourceCategories.forEach((category) => {
      if (category.id) {
        map.set(category.id, category.name)
      }
    })
    return map
  }, [packageSourceCategories])
  const categoryOrderById = useMemo(() => {
    const map = new Map<string, number>()
    packageSourceCategories.forEach((category) => {
      if (category.id) {
        map.set(category.id, category.displayOrder)
      }
    })
    return map
  }, [packageSourceCategories])
  const selectedPeriodLabel = useMemo(() => {
    if (!selectedPeriodId) return ''
    const period = findPeriodById(periodsResponse?.periods || [], selectedPeriodId)
    return period?.label || selectedPeriodId
  }, [periodsResponse?.periods, selectedPeriodId])

  // Determine if agent is thinking (processing but no final response yet)
  const isAgentThinking = useMemo(() => {
    if (!sessionId) return false
    if (activeAgents.size > 0) return true
    const lastMessage = messages[messages.length - 1]
    if (phase === 'processing' && lastMessage?.role === 'user') return true
    // In processing phase, show thinking unless we hit a completed status
    if (phase === 'processing') {
      const completedStatuses = [
        'Ready for review',
        'Categories draft ready',
        'Rewards draft ready',
        'Rewards approved. Awaiting next step...',
        'Employee match review ready',
      ]
      if (processingStatus && completedStatuses.includes(processingStatus)) return false
      return true
    }
    return false
  }, [sessionId, phase, processingStatus, messages, activeAgents])
  const fileInputRef = useRef<HTMLInputElement>(null)
  const hasDraftResults = categories.length > 0 || rewards.length > 0
  const hasApprovedResults = (approvedCategories?.length ?? 0) > 0 || (approvedRewards?.length ?? 0) > 0
  const unresolvedRewardCategoryCount = useMemo(
    () => rewards.filter((reward) => !isUUID(reward.categoryId)).length,
    [rewards]
  )
  const hasUnresolvedRewardCategories = unresolvedRewardCategoryCount > 0
  const showNoChanges =
    phase === 'processing' &&
    !!sessionId &&
    !isAgentThinking &&
    processingStatus === 'Ready for review' &&
    !hasDraftResults &&
    !hasApprovedResults
  const processingTitle = showNoChanges ? 'No changes found' : 'Smart Import in progress'
  const processingMessage = showNoChanges ? 'Everything in this file already exists.' : processingStatus || undefined

  useEffect(() => {
    latestCategoriesRef.current = categories
  }, [categories])

  useEffect(() => {
    latestApprovedCategoriesRef.current = approvedCategories
  }, [approvedCategories])

  const scrollToBottom = useCallback(() => {
    pageScrollAnchorRef.current?.scrollIntoView({ behavior: 'smooth', block: 'end' })
  }, [])

  useEffect(() => {
    scrollToBottom()
    const timeoutId = window.setTimeout(scrollToBottom, 360)
    return () => window.clearTimeout(timeoutId)
  }, [
    approvedCategories?.length,
    approvedRewards?.length,
    categories.length,
    isAgentThinking,
    messages.length,
    phase,
    processingStatus,
    rewards.length,
    scrollToBottom,
  ])

  const importOptions = useMemo<SmartImportOption[]>(
    () => [
      {
        id: 'categories-rewards',
        title: 'Categories & Rewards',
        description: 'Auto-create reward categories and individual rewards from your file',
        icon: <FileTextOutlined style={{ fontSize: 24 }} />,
        color: theme.palette.primary.main,
      },
      {
        id: 'reward-package',
        title: 'Reward Package',
        description: 'Build a pre-configured bundle of rewards for employees',
        icon: <ThunderboltOutlined style={{ fontSize: 24 }} />,
        color: theme.palette.success.main,
      },
      {
        id: 'employee-reward-package',
        title: 'Employee Reward Package',
        description: 'Map employees and assign reward packages from a spreadsheet import',
        icon: <ThunderboltOutlined style={{ fontSize: 24 }} />,
        color: theme.palette.info.main,
      },
    ],
    [theme.palette.info.main, theme.palette.primary.main, theme.palette.success.main]
  )

  const mergeEmployeeOptions = (
    current: EmployeeMatchUserOption[],
    next: EmployeeMatchUserOption[]
  ): EmployeeMatchUserOption[] => {
    const merged = new Map<string, EmployeeMatchUserOption>()
    current.forEach((option) => merged.set(option.id, option))
    next.forEach((option) => {
      if (!option.id) return
      merged.set(option.id, {
        ...(merged.get(option.id) || option),
        ...option,
      })
    })
    return Array.from(merged.values()).sort((a, b) => a.name.localeCompare(b.name))
  }

  const normalizeEmployeeOption = (candidate: any): EmployeeMatchUserOption | null => {
    if (!candidate || typeof candidate !== 'object') return null
    const id = String(candidate.id ?? candidate.user_id ?? candidate.userId ?? '').trim()
    if (!id) return null
    const name =
      String(
        candidate.name ??
          candidate.full_name ??
          candidate.fullName ??
          `${String(candidate.first_name ?? candidate.firstName ?? '').trim()} ${String(
            candidate.last_name ?? candidate.lastName ?? ''
          ).trim()}`
      ).trim() || id
    const emailValue = String(candidate.email ?? '').trim()
    const externalIdValue = String(candidate.external_id ?? candidate.externalId ?? '').trim()
    const teamNameValue = String(
      candidate.team_name ??
        candidate.teamName ??
        candidate.organization_name ??
        candidate.organizationName ??
        candidate.org_name ??
        candidate.orgName ??
        candidate.department_name ??
        candidate.departmentName ??
        candidate.department ??
        candidate.organization?.name ??
        ''
    ).trim()

    return {
      id,
      name,
      teamName: teamNameValue || undefined,
      email: emailValue || undefined,
      externalId: externalIdValue || undefined,
    }
  }

  const extractEmployeeUserOptions = (result: any): EmployeeMatchUserOption[] => {
    if (!result) return []
    const sourceArrays = [result?.data, result?.users, result?.rows, result]
    for (const source of sourceArrays) {
      if (!Array.isArray(source)) continue
      return source
        .map(normalizeEmployeeOption)
        .filter((option): option is EmployeeMatchUserOption => option !== null)
        .sort((a, b) => a.name.localeCompare(b.name))
    }
    return []
  }

  const extractEmployeeMatchCandidates = (result: any): EmployeeMatchUserOption[] => {
    if (!result) return []
    const rows = Array.isArray(result?.rows) ? result.rows : Array.isArray(result?.data?.rows) ? result.data.rows : []

    const candidates: EmployeeMatchUserOption[] = []
    rows.forEach((row: any) => {
      const matched = normalizeEmployeeOption(row?.matched_user ?? row?.matchedUser)
      if (matched) {
        candidates.push(matched)
      }
      const rowCandidates = row?.candidate_users ?? row?.candidateUsers
      if (Array.isArray(rowCandidates)) {
        rowCandidates.forEach((candidate: any) => {
          const normalized = normalizeEmployeeOption(candidate)
          if (normalized) {
            candidates.push(normalized)
          }
        })
      }
    })
    return candidates
  }

  const extractRewardPackageOptions = (result: any): ExistingPackageOption[] => {
    const packages = Array.isArray(result?.data) ? result.data : Array.isArray(result?.packages) ? result.packages : []

    return packages
      .map((pkg: any): ExistingPackageOption | null => {
        const id = String(pkg?.id ?? '').trim()
        if (!id) return null
        const name = String(pkg?.name ?? 'Unnamed package').trim() || 'Unnamed package'
        const rewards = Array.isArray(pkg?.rewards)
          ? pkg.rewards
              .map((reward: any) => {
                const rewardId = String(reward?.reward_id ?? reward?.rewardId ?? '').trim()
                if (!rewardId) return null
                return {
                  rewardId,
                  displayOrder: Number(reward?.display_order ?? reward?.displayOrder ?? 0) || 0,
                  companyContribution: Number(reward?.company_contribution ?? reward?.companyContribution ?? 0) || 0,
                  employeeContribution: Number(reward?.employee_contribution ?? reward?.employeeContribution ?? 0) || 0,
                }
              })
              .filter(
                (
                  reward: ExistingPackageOption['rewards'][number] | null
                ): reward is ExistingPackageOption['rewards'][number] => reward !== null
              )
          : []

        return { id, name, rewards }
      })
      .filter((pkg: ExistingPackageOption | null): pkg is ExistingPackageOption => pkg !== null)
  }

  const extractEmployeeMatches = (result: any): EmployeeSourceRowMatchResult[] => {
    if (!result) return []

    const rows = Array.isArray(result?.rows)
      ? result.rows
      : Array.isArray(result?.data?.rows)
        ? result.data.rows
        : Array.isArray(result)
          ? result
          : []

    return rows.map((row: any, index: number) => {
      const rowId = String(row?.row_id ?? row?.rowId ?? row?.source_reference?.row_id ?? `row-${index + 1}`)
      const rowNumberMatch = /^row-(\d+)$/i.exec(rowId)
      const rowNumber = rowNumberMatch ? parseInt(rowNumberMatch[1], 10) : index + 1
      const sourceReference = row?.source_reference ?? row?.sourceReference ?? {}

      const firstName = String(sourceReference?.first_name ?? sourceReference?.firstName ?? '').trim()
      const lastName = String(sourceReference?.last_name ?? sourceReference?.lastName ?? '').trim()
      const sourceFullName = String(sourceReference?.full_name ?? sourceReference?.fullName ?? '').trim()
      const employeeName = sourceFullName || `${firstName} ${lastName}`.trim() || `Row ${rowNumber}`
      const employeeEmail = String(sourceReference?.email ?? sourceReference?.employee_email ?? '').trim() || undefined
      const employeeExternalId =
        String(sourceReference?.external_id ?? sourceReference?.externalId ?? '').trim() || undefined

      const matchedUser = normalizeEmployeeOption(row?.matched_user ?? row?.matchedUser)
      const statusRaw = String(row?.status ?? '').toLowerCase()
      const status: EmployeeSourceRowMatchResult['status'] =
        statusRaw === 'matched' ? 'matched' : statusRaw === 'skipped' ? 'skipped' : 'unresolved'
      const confidence =
        typeof row?.confidence === 'number'
          ? row.confidence
          : typeof row?.confidence === 'string'
            ? Number(row.confidence)
            : undefined
      const rawValuesSource = sourceReference?.raw_values ?? sourceReference?.rawValues
      const rawValues =
        rawValuesSource && typeof rawValuesSource === 'object' && !Array.isArray(rawValuesSource)
          ? Object.entries(rawValuesSource as Record<string, unknown>).reduce<
              Record<string, string | number | boolean | null>
            >((acc, [key, value]) => {
              if (
                value === null ||
                typeof value === 'string' ||
                typeof value === 'number' ||
                typeof value === 'boolean'
              ) {
                acc[key] = value
              }
              return acc
            }, {})
          : undefined

      return {
        rowId,
        status,
        assignedUserId: matchedUser?.id,
        matchedUser: matchedUser || undefined,
        confidence: Number.isFinite(confidence) ? confidence : undefined,
        reason: typeof row?.reason === 'string' ? row.reason : undefined,
        sourceRow: {
          rowId,
          rowNumber,
          employeeName,
          employeeEmail,
          employeeExternalId,
          rewardName: String(sourceReference?.reward_name ?? sourceReference?.rewardName ?? '').trim() || undefined,
          categoryName:
            String(sourceReference?.category_name ?? sourceReference?.categoryName ?? '').trim() || undefined,
          rawValues,
        },
      }
    })
  }

  // Subscribe to SSE events for agent responses
  useEffect(() => {
    const unsubscribeProcessing = subscribe('agent_processing', (event: SSEEvent) => {
      console.log('[SmartImport] Agent processing event:', event)
    })

    const unsubscribeAgentStarted = subscribe('agent.started', (event: SSEEvent) => {
      console.log('[SmartImport] Agent started:', event)
      setProcessingStatus('Starting import analysis')
      const agentName = event.data?.agent_name
      if (agentName) {
        setActiveAgents((current) => {
          const next = new Set(current)
          next.add(agentName)
          return next
        })
      }
    })

    const unsubscribeModelStarted = subscribe('model.response_started', (event: SSEEvent) => {
      console.log('[SmartImport] Model response started:', event)
      setProcessingStatus((current) => {
        if (!current) return 'Reviewing your file'
        if (
          current === 'Uploading file' ||
          current === 'Starting import analysis' ||
          current === 'Reviewing your file'
        ) {
          return 'Reviewing your file'
        }
        return current
      })
    })

    const unsubscribeModelCompleted = subscribe('model.response_completed', (event: SSEEvent) => {
      console.log('[SmartImport] Model response completed:', event)
      setProcessingStatus((current) => {
        if (!current) return 'Response ready'
        if (
          current === 'Uploading file' ||
          current === 'Starting import analysis' ||
          current === 'Reviewing your file'
        ) {
          return 'Response ready'
        }
        return current
      })

      const responseText = event.data?.response_preview?.text
      if (responseText) {
        const packageDecision = extractPackageDecision(responseText)
        if (packageDecision.recommendedPackageId) {
          setRecommendedExistingPackageId(packageDecision.recommendedPackageId)
        }
        if (packageDecision.matchedPackageIds.length > 0) {
          setMatchedPackageCandidateIds((current) =>
            Array.from(new Set([...current, ...packageDecision.matchedPackageIds]))
          )
        }

        const sanitized = sanitizeAgentResponse(responseText)
        if (!sanitized) return
        const timestamp = new Date(event.timestamp || Date.now())
        setMessages((current) => [
          ...current,
          {
            id: `${timestamp.getTime()}-agent`,
            role: 'agent',
            content: sanitized,
            timestamp,
          },
        ])
      }
    })

    const unsubscribeToolStarted = subscribe('tool.execution_started', (event: SSEEvent) => {
      console.log('[SmartImport] Tool execution started:', event)
      const toolName = event.data?.tool_name
      if (toolName === 'transfer_to_agent') {
        setProcessingStatus('Handing off to the import agent')
      }

      if (toolName === 'get_categories') {
        setProcessingStatus('Looking up existing categories')
      }

      if (toolName === 'get_rewards') {
        setProcessingStatus('Looking up existing rewards')
      }

      if (toolName === 'get_reward_packages') {
        setProcessingStatus('Looking up existing reward packages')
      }

      if (toolName === 'prepare_categories_upsert') {
        setProcessingStatus('Drafting category changes')
      }

      if (toolName === 'prepare_rewards_upsert') {
        setProcessingStatus('Drafting reward changes')
      }

      if (toolName === 'get_organization_users') {
        setProcessingStatus('Loading organization users')
      }

      if (toolName === 'prepare_employee_matches') {
        setProcessingStatus('Preparing employee matches')
      }

      if (toolName === 'prepare_employee_reward_assignments') {
        setProcessingStatus('Preparing employee reward assignments')
      }
    })

    const unsubscribeToolCompleted = subscribe('tool.execution_completed', (event: SSEEvent) => {
      console.log('[SmartImport] Tool execution completed:', event)
      const toolName = event.data?.tool_name
      const result = event.data?.result

      if (toolName === 'get_categories' && result?.meta) {
        const count = result.meta.count ?? 0
        setProcessingStatus(`Checked existing categories (${count})`)
      }

      if (toolName === 'get_rewards' && result?.meta) {
        const count = result.meta.count ?? 0
        setProcessingStatus(`Checked existing rewards (${count})`)
      }

      if (toolName === 'get_reward_packages' && result?.meta) {
        const count = result.meta.count ?? 0
        setProcessingStatus(`Checked existing reward packages (${count})`)

        const nextPackageOptions = extractRewardPackageOptions(result)
        setExistingPackageOptions(nextPackageOptions)
        setSelectedExistingPackageId((current) => {
          if (current && nextPackageOptions.some((pkg) => pkg.id === current)) {
            return current
          }
          return ''
        })
      }

      if (toolName === 'prepare_categories_upsert' && result?.categories) {
        const nextCategories = result.categories as Array<any>
        setCategories(
          nextCategories.map((category) => ({
            id: category.id || undefined,
            tempId: category.id ? undefined : `cat-${category.name}-${Math.random()}`,
            name: category.name,
            displayOrder: category.display_order ?? category.displayOrder ?? 1,
            isNew: !category.id,
          }))
        )
        pendingPhaseRef.current = 'review-categories'
        setProcessingStatus('Categories draft ready')
      }

      if (toolName === 'prepare_rewards_upsert' && result?.rewards) {
        const nextRewards = result.rewards as Array<any>
        const categoriesById = new Map<string, CategoryData>()
        const categoriesByName = new Map<string, CategoryData>()
        latestCategoriesRef.current.forEach((category) => {
          if (category.id) categoriesById.set(category.id, category)
          categoriesByName.set(category.name.toLowerCase(), category)
        })
        const holdForCategories =
          !latestApprovedCategoriesRef.current && pendingPhaseRef.current === 'review-categories'
        const mappedRewards = nextRewards.map((reward) => {
          const rawCategoryId = String(
            reward.category_id ?? reward.categoryId ?? reward.category_name ?? reward.categoryName ?? ''
          ).trim()
          const explicitCategoryName = String(reward.category_name ?? reward.categoryName ?? '').trim()
          const category = resolveCategoryReference(
            rawCategoryId,
            explicitCategoryName,
            categoriesById,
            categoriesByName,
            latestCategoriesRef.current
          )
          const categoryName =
            category?.name ||
            (typeof reward.category_name === 'string' ? reward.category_name : undefined) ||
            (typeof reward.categoryName === 'string' ? reward.categoryName : undefined)
          const categoryId = category?.id || rawCategoryId
          const supportsEmployeeContributionRaw =
            reward.supports_employee_contribution ?? reward.supportsEmployeeContribution

          return {
            id: reward.id || undefined,
            tempId: reward.id ? undefined : `reward-${reward.name}-${Math.random()}`,
            name: reward.name,
            categoryId,
            categoryName,
            displayOrder: reward.display_order ?? reward.displayOrder ?? 1,
            supportsEmployeeContribution: !!supportsEmployeeContributionRaw,
            isNew: !reward.id,
          }
        })

        setRewards(mappedRewards)
        if (!holdForCategories) {
          pendingPhaseRef.current = 'review-rewards'
          setProcessingStatus('Rewards draft ready')
        }
      }

      if (toolName === 'get_organization_users') {
        const nextOptions = extractEmployeeUserOptions(result)
        if (nextOptions.length > 0) {
          setEmployeeMatchUserOptions((current) => mergeEmployeeOptions(current, nextOptions))
          setProcessingStatus(`Loaded organization users (${nextOptions.length})`)
        }
      }

      if (toolName === 'prepare_employee_matches' || toolName === 'prepare_employee_reward_assignments') {
        const nextMatches = extractEmployeeMatches(result)
        if (nextMatches.length > 0) {
          setEmployeeMatches(nextMatches)
          const candidateOptions = extractEmployeeMatchCandidates(result)
          if (candidateOptions.length > 0) {
            setEmployeeMatchUserOptions((current) => mergeEmployeeOptions(current, candidateOptions))
          }
          pendingPhaseRef.current = 'review-employee-matches'
          setProcessingStatus('Employee match review ready')
        }
      }
    })

    const unsubscribeToolFailed = subscribe('tool.execution_failed', (event: SSEEvent) => {
      const toolName = event.data?.tool_name
      if (
        toolName === 'prepare_categories_upsert' ||
        toolName === 'prepare_rewards_upsert' ||
        toolName === 'prepare_employee_matches' ||
        toolName === 'prepare_employee_reward_assignments'
      ) {
        console.warn('[SmartImport] Draft preparation tool failed:', event)
      }
    })

    const unsubscribeAgentCompleted = subscribe('agent.completed', (event: SSEEvent) => {
      console.log('[SmartImport] Agent completed:', event)
      setProcessingStatus('Ready for review')
      const agentName = event.data?.agent_name
      setActiveAgents((current) => {
        const next = new Set(current)
        if (agentName) {
          next.delete(agentName)
        } else {
          next.clear()
        }
        return next
      })
      if (pendingPhaseRef.current) {
        setPhase(pendingPhaseRef.current)
        pendingPhaseRef.current = null
      }
    })

    const unsubscribeError = subscribe('agent_error', (event: SSEEvent) => {
      console.error('[SmartImport] Agent error:', event)
      setProcessingStatus(event.data?.message || 'Agent error encountered')
      setActiveAgents(new Set())
      pendingPhaseRef.current = null
      setPhase('welcome')
    })

    const unsubscribeModelFailed = subscribe('model.response_failed', (event: SSEEvent) => {
      console.error('[SmartImport] Model response failed:', event)
      setProcessingStatus(event.data?.error_message || 'Model response failed')
      setActiveAgents(new Set())
      pendingPhaseRef.current = null
      setPhase('welcome')
    })

    const unsubscribeAgentFailed = subscribe('agent.failed', (event: SSEEvent) => {
      console.error('[SmartImport] Agent failed:', event)
      setProcessingStatus('Agent failed')
      const agentName = event.data?.agent_name
      setActiveAgents((current) => {
        const next = new Set(current)
        if (agentName) {
          next.delete(agentName)
        } else {
          next.clear()
        }
        return next
      })
      pendingPhaseRef.current = null
      setPhase('welcome')
    })

    return () => {
      unsubscribeProcessing()
      unsubscribeAgentStarted()
      unsubscribeModelStarted()
      unsubscribeModelCompleted()
      unsubscribeToolStarted()
      unsubscribeToolCompleted()
      unsubscribeToolFailed()
      unsubscribeAgentCompleted()
      unsubscribeError()
      unsubscribeModelFailed()
      unsubscribeAgentFailed()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [subscribe])

  useEffect(() => {
    if (createdPackageId) return

    setSelectedExistingPackageId((current) => {
      if (current && matchedExistingPackageOptions.some((pkg) => pkg.id === current)) {
        return current
      }

      if (matchedExistingPackageOptions.length === 1) {
        return matchedExistingPackageOptions[0].id
      }

      if (
        recommendedExistingPackageId &&
        matchedExistingPackageOptions.some((pkg) => pkg.id === recommendedExistingPackageId)
      ) {
        return recommendedExistingPackageId
      }

      return ''
    })
  }, [createdPackageId, recommendedExistingPackageId, matchedExistingPackageOptions])

  // Handlers
  const handleSelectOption = (optionId: SmartImportOptionId) => {
    if (optionId === 'employee-reward-package' && uploadedFile && getFileTypeExtension(uploadedFile) === 'pdf') {
      setProcessingStatus('Employee reward package import only supports CSV or Excel files.')
      return
    }

    setSelectedOption(optionId)
    if (optionId === 'employee-reward-package') {
      setPhase('select-period')
      setProcessingStatus(null)
      return
    }
    handleStartProcessing(optionId)
  }

  const handleFileSelect = (file: File) => {
    const allowedTypes = [
      'text/csv',
      'application/vnd.ms-excel',
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      'application/pdf',
    ]
    if (!allowedTypes.includes(file.type)) {
      setProcessingStatus('Unsupported file type. Upload CSV, Excel, or PDF.')
      return
    }
    setUploadedFile(file)
    setSelectedOption(null)
    setSelectedPeriodId('')
    setSelectedRootPeriodId('')
    setEmployeeMatches([])
    setEmployeeMatchUserOptions([])
    setEmployeeMatchError(null)
    setEmployeeRewardSummary(null)
    setIsCreatingEmployeeRewards(false)
    setHasRetriedEmptyEmployeeImport(false)
    setHasRetriedNoPackageDraftFlow(false)
    setCreatedPackageId(null)
    setExistingPackageOptions([])
    setSelectedExistingPackageId('')
    setRecommendedExistingPackageId(null)
    setMatchedPackageCandidateIds([])
    setPhase('select-type')
  }

  const handleStartProcessing = async (optionOverride?: SmartImportOptionId) => {
    const importType = optionOverride || selectedOption
    if (!uploadedFile || !importType || !token || !profile) return

    const fileType = getFileTypeExtension(uploadedFile)
    const isEmployeeImport = importType === 'employee-reward-package'
    if (isEmployeeImport && !['csv', 'xls', 'xlsx'].includes(fileType)) {
      setProcessingStatus('Employee reward package import only supports CSV or Excel files.')
      setPhase('select-type')
      return
    }
    if (isEmployeeImport && (!selectedPeriodId || !selectedRootPeriodId)) {
      setProcessingStatus('Please select a period before processing this import.')
      setPhase('select-period')
      return
    }

    setMessages([])
    setActiveAgents(new Set())
    setEmployeeMatches([])
    setEmployeeMatchUserOptions([])
    setEmployeeMatchError(null)
    setEmployeeRewardSummary(null)
    setIsCreatingEmployeeRewards(false)
    setHasRetriedEmptyEmployeeImport(false)
    setHasRetriedNoPackageDraftFlow(false)
    setCreatedPackageId(null)
    setExistingPackageOptions([])
    setSelectedExistingPackageId('')
    setRecommendedExistingPackageId(null)
    setMatchedPackageCandidateIds([])

    const selectedOrgId = purcentAppState?.selectedOrganization?.id
    const rootOrgId = getRootOrganizationId(profile)

    if (!selectedOrgId || !rootOrgId) {
      setProcessingStatus('Please select an organization first.')
      return
    }

    setIsUploadingFile(true)
    setProcessingStatus('Uploading file')
    setPhase('processing')

    try {
      const base64Data = await fileToBase64(uploadedFile)
      const response = await createAgentSession(token, {
        organization_id: selectedOrgId,
        root_organization_id: rootOrgId,
        period_id: isEmployeeImport ? selectedPeriodId : undefined,
        root_period_id: isEmployeeImport ? selectedRootPeriodId : undefined,
        import_type: importType,
        file_data: base64Data,
        file_name: uploadedFile.name,
        file_type: fileType,
      })

      setSessionId(response.session_id)
      setProcessingStatus(null)
    } catch (error) {
      console.error('Failed to upload file:', error)
      setProcessingStatus('Upload failed. Please try again.')
      pendingPhaseRef.current = null
      setPhase('welcome')
    } finally {
      setIsUploadingFile(false)
    }
  }

  const handleBackToUpload = () => {
    setPhase('welcome')
    setUploadedFile(null)
    setSelectedOption(null)
    setCategories([])
    setRewards([])
    setApprovedCategories(null)
    setApprovedRewards(null)
    setMessages([])
    setChatMessage('')
    setSessionId(null)
    setActiveAgents(new Set())
    setPackageName('')
    setPackageDescription('')
    setPackageDisclaimer('')
    setPackageError(null)
    setCreatedPackageId(null)
    setPackageStep(0)
    setPackageRewardItems(new Map())
    setIsPackageInitialized(false)
    setSelectedPeriodId('')
    setSelectedRootPeriodId('')
    setEmployeeMatches([])
    setEmployeeMatchUserOptions([])
    setEmployeeMatchError(null)
    setEmployeeRewardSummary(null)
    setIsCreatingEmployeeRewards(false)
    setHasRetriedEmptyEmployeeImport(false)
    setHasRetriedNoPackageDraftFlow(false)
    setExistingPackageOptions([])
    setSelectedExistingPackageId('')
    setRecommendedExistingPackageId(null)
    setMatchedPackageCandidateIds([])
    pendingPhaseRef.current = null
    setProcessingStatus(null)
  }

  const handleCategoryEdit = (id: string, field: keyof CategoryData, value: string | number) => {
    setCategories((current) =>
      current.map((category) => {
        const categoryId = category.id || category.tempId
        if (categoryId !== id) return category
        return {
          ...category,
          [field]: value,
        }
      })
    )
  }

  const handleCategoryReorder = (nextCategories: CategoryData[]) => {
    setCategories(nextCategories)
  }

  const handleRewardsReorder = (nextRewards: RewardData[]) => {
    setRewards(nextRewards)
  }

  const handleAssignEmployeeMatchUser = useCallback(
    (rowId: string, userId: string) => {
      setEmployeeMatchError(null)
      setEmployeeMatches((current) =>
        current.map((row) => {
          if (row.rowId !== rowId) return row
          if (!userId) {
            return {
              ...row,
              status: 'unresolved',
              assignedUserId: undefined,
              matchedUser: undefined,
            }
          }

          const userOption = employeeMatchUserOptions.find((option) => option.id === userId)
          const fallbackUser = userOption || { id: userId, name: userId }

          return {
            ...row,
            status: 'matched',
            assignedUserId: userId,
            matchedUser: fallbackUser,
          }
        })
      )
    },
    [employeeMatchUserOptions]
  )

  const handleSkipEmployeeMatchRow = useCallback((rowId: string) => {
    setEmployeeMatchError(null)
    setEmployeeMatches((current) =>
      current.map((row) =>
        row.rowId === rowId
          ? {
              ...row,
              status: 'skipped',
              assignedUserId: undefined,
              matchedUser: undefined,
            }
          : row
      )
    )
  }, [])

  const sendAgentInstruction = useCallback(
    async (content: string, showInChat: boolean = true) => {
      const trimmed = content.trim()
      if (!trimmed || !sessionId || !token) return

      if (showInChat) {
        const timestamp = new Date()
        setMessages((current) => [
          ...current,
          {
            id: `${timestamp.getTime()}-user`,
            role: 'user',
            content: trimmed,
            timestamp,
          },
        ])
      }

      try {
        await sendAgentMessage(token, sessionId, trimmed)
      } catch (error) {
        console.error('Failed to send message:', error)
        setProcessingStatus('Failed to send message to agent.')
      }
    },
    [sessionId, token]
  )

  const handleSendMessage = async (message: string) => {
    const trimmed = message.trim()
    if (!trimmed) return
    setChatMessage('')
    await sendAgentInstruction(trimmed, true)
  }

  useEffect(() => {
    if (selectedOption !== 'employee-reward-package') return
    if (phase !== 'processing') return
    if (!sessionId || isAgentThinking) return
    if (hasRetriedEmptyEmployeeImport) return
    if (processingStatus !== 'Ready for review') return

    const hasEmployeeResults = employeeMatches.length > 0 || employeeRewardSummary !== null
    if (hasDraftResults || hasApprovedResults || hasEmployeeResults) {
      return
    }

    setHasRetriedEmptyEmployeeImport(true)
    setProcessingStatus('Retrying employee import analysis...')
    void sendAgentInstruction(
      'Retry required: the previous pass returned no usable output. Continue employee reward package workflow now: call get_organization_users, then prepare_employee_matches with imported_rows (including raw_values), then call get_reward_packages and return a concise recommendation.',
      false
    )
  }, [
    selectedOption,
    phase,
    sessionId,
    isAgentThinking,
    hasRetriedEmptyEmployeeImport,
    processingStatus,
    employeeMatches.length,
    employeeRewardSummary,
    hasDraftResults,
    hasApprovedResults,
    sendAgentInstruction,
  ])

  useEffect(() => {
    if (selectedOption !== 'employee-reward-package') return
    if (phase !== 'review-employee-matches') return
    if (!sessionId || !token || isAgentThinking) return
    if (hasRetriedNoPackageDraftFlow) return
    if (processingStatus !== 'Ready for review') return
    if (createdPackageId || matchedPackageCount > 0) return
    if (employeeMatches.length === 0) return
    if (hasDraftResults || hasApprovedResults) return

    setHasRetriedNoPackageDraftFlow(true)
    setEmployeeMatchError(null)
    setPhase('processing')
    setProcessingStatus('No existing package found. Preparing categories and rewards for a new package...')
    void sendAgentInstruction(
      'No existing reward package was found for this employee import. Do not stop for confirmation. Continue immediately by calling get_categories and get_rewards, then prepare_categories_upsert and prepare_rewards_upsert from the uploaded file, then continue to package drafting for review.',
      false
    )
  }, [
    selectedOption,
    phase,
    sessionId,
    token,
    isAgentThinking,
    hasRetriedNoPackageDraftFlow,
    processingStatus,
    createdPackageId,
    matchedPackageCount,
    employeeMatches.length,
    hasDraftResults,
    hasApprovedResults,
    sendAgentInstruction,
  ])

  const handlePackageToggleReward = useCallback((rewardId: string) => {
    setPackageRewardItems((prev) => {
      const next = new Map(prev)
      const item = next.get(rewardId)
      if (item) {
        next.set(rewardId, { ...item, selected: !item.selected })
      }
      return next
    })
  }, [])

  const handlePackageContributionChange = useCallback(
    (rewardId: string, field: 'companyContribution' | 'employeeContribution', value: string) => {
      const numericValue = parseFloat(value.replace(/,/g, '')) || 0
      setPackageRewardItems((prev) => {
        const next = new Map(prev)
        const item = next.get(rewardId)
        if (item) {
          next.set(rewardId, {
            ...item,
            [field]: numericValue,
          })
        }
        return next
      })
    },
    []
  )

  const handlePackageReorderRewards = useCallback((categoryName: string, oldIndex: number, newIndex: number) => {
    setPackageRewardItems((prev) => {
      const next = new Map(prev)
      const categoryRewards = Array.from(next.values())
        .filter((item) => item.categoryName === categoryName)
        .sort((a, b) => a.rewardDisplayOrder - b.rewardDisplayOrder)

      const [movedItem] = categoryRewards.splice(oldIndex, 1)
      categoryRewards.splice(newIndex, 0, movedItem)

      categoryRewards.forEach((item, index) => {
        next.set(item.rewardId, {
          ...item,
          rewardDisplayOrder: index,
        })
      })

      return next
    })
  }, [])

  const handlePackageToggleAll = useCallback((checked: boolean) => {
    setPackageRewardItems((prev) => {
      const next = new Map(prev)
      next.forEach((item, key) => {
        next.set(key, { ...item, selected: checked })
      })
      return next
    })
  }, [])

  const handlePackageNext = useCallback(() => {
    setPackageStep((current) => current + 1)
  }, [])

  const handlePackageBack = useCallback(() => {
    setPackageStep((current) => Math.max(0, current - 1))
  }, [])

  const handleCreatePackage = async () => {
    if (!token || !rootOrganizationId) return
    if (!packageName.trim()) {
      setPackageError('Package name is required.')
      return
    }
    if (packageSelectedRewards.length === 0) {
      setPackageError('No rewards selected for this package.')
      return
    }
    if (missingPackageRewardsCount > 0) {
      setPackageError('Some rewards are still being matched. Please try again in a moment.')
      return
    }

    setPackageError(null)
    try {
      const selectedRewards = packageSelectedRewards.map((item) => ({
        rewardId: item.rewardId,
        displayOrder: item.rewardDisplayOrder,
        companyContribution: item.companyContribution,
        employeeContribution: item.employeeContribution,
      }))

      const createdPackage = await createPackage({
        rootOrganizationId,
        organizationId: rootOrganizationId,
        name: packageName.trim(),
        description: packageDescription.trim() || null,
        disclaimer: packageDisclaimer.trim() || null,
        rewards: selectedRewards,
      })
      const newPackageId = createdPackage.id || null

      invalidateRewardPackagesCache(rootOrganizationId, token)

      if (selectedOption === 'employee-reward-package') {
        if (!newPackageId) {
          setPackageError('Package was created but no package ID was returned.')
          return
        }
        setCreatedPackageId(newPackageId)
        setSelectedExistingPackageId('')
        setRecommendedExistingPackageId(null)
        setMatchedPackageCandidateIds([])
        setEmployeeMatches([])
        setEmployeeRewardSummary(null)
        setEmployeeMatchError(null)
        setPhase('processing')
        setProcessingStatus('Package created. Preparing employee matches...')
        await sendAgentInstruction(
          `Reward package created (id: ${newPackageId}). Now prepare employee matching results from the uploaded file using prepare_employee_matches and return matched, ambiguous, and unmatched rows for review.`,
          false
        )
        return
      }

      navigate('/rewards/packages')
    } catch (error: any) {
      setPackageError(error?.message || 'Failed to create package.')
    }
  }

  const handleCreateNewPackageInstead = async () => {
    setSelectedExistingPackageId('')
    setEmployeeMatchError(null)
    setHasRetriedNoPackageDraftFlow(true)
    setPhase('processing')
    setProcessingStatus('Preparing categories and rewards for a new package...')

    await sendAgentInstruction(
      'Do not reuse an existing package for this import. Continue immediately by calling get_categories and get_rewards, then prepare_categories_upsert and prepare_rewards_upsert from the uploaded file, then proceed to reward package drafting for review.',
      false
    )
  }

  const handleCreateEmployeeRewards = useCallback(async () => {
    if (!token || !selectedPeriodId || !selectedRootPeriodId) return

    const packageIdToAssign = effectivePackageId
    if (!packageIdToAssign) {
      setEmployeeMatchError('Select an existing reward package or create a new one before assigning employees.')
      return
    }

    const selectedOrgId = purcentAppState?.selectedOrganization?.id || rootOrganizationId
    if (!selectedOrgId) {
      setEmployeeMatchError('Missing organization context for employee rewards assignment.')
      return
    }

    if (unresolvedEmployeeMatchCount > 0) {
      setEmployeeMatchError('Resolve or skip all unresolved employee rows before creating employee rewards.')
      return
    }

    const assignmentsByUserId = new Map<string, EmployeeSourceRowMatchResult>()
    matchedEmployeeRows.forEach((row) => {
      const userId = row.assignedUserId || row.matchedUser?.id || ''
      if (!userId) return
      assignmentsByUserId.set(userId, row)
    })

    const matchedAssignments = Array.from(assignmentsByUserId.entries()).map(([userId, row]) => ({
      userId,
      row,
    }))
    const totalAssignments = matchedAssignments.length

    if (totalAssignments === 0) {
      setEmployeeMatchError('No matched employees found. Assign at least one employee to continue.')
      return
    }

    if (activePackageRewards.length === 0) {
      setEmployeeMatchError('Selected package has no rewards to assign.')
      return
    }

    const buildUserRewardsFromRow = (row: EmployeeSourceRowMatchResult): UserRewardItem[] | null => {
      const parsedValues = parseSpreadsheetContributionValues(row.sourceRow.rawValues)
      if (parsedValues.size === 0) {
        return null
      }

      let usedSpreadsheetValue = false
      const rewards: UserRewardItem[] = []
      activePackageRewards.forEach((packageReward) => {
        const rewardMeta = rewardsCatalogById.get(packageReward.rewardId)
        if (!rewardMeta) {
          return
        }

        const rewardId = rewardMeta.id || packageReward.rewardId
        if (!rewardId) {
          return
        }

        const parsed = parsedValues.get(normalizeRewardLabel(rewardMeta.name))
        const companyContribution = parsed?.company ?? parsed?.single ?? packageReward.companyContribution
        const employeeContribution = parsed?.employee ?? packageReward.employeeContribution

        if (parsed && (parsed.company !== undefined || parsed.employee !== undefined || parsed.single !== undefined)) {
          usedSpreadsheetValue = true
        }

        rewards.push({
          rewardId,
          rewardName: rewardMeta.name,
          rewardDisplayOrder: rewardMeta.displayOrder,
          categoryId: rewardMeta.categoryId,
          categoryName:
            rewardMeta.category?.name ||
            categoryNameById.get(rewardMeta.categoryId) ||
            row.sourceRow.categoryName ||
            'Uncategorized',
          categoryDisplayOrder: rewardMeta.category?.displayOrder || categoryOrderById.get(rewardMeta.categoryId) || 0,
          companyContribution,
          employeeContribution,
        })
      })

      if (!usedSpreadsheetValue || rewards.length === 0) {
        return null
      }

      return rewards
    }

    setEmployeeMatchError(null)
    setEmployeeRewardSummary(null)
    setIsCreatingEmployeeRewards(true)
    setProcessingStatus(`Creating employee rewards (0/${totalAssignments})`)

    try {
      let successful = 0
      let failed = 0
      const failedUserIds: string[] = []
      const batchSize = 5

      for (let i = 0; i < totalAssignments; i += batchSize) {
        const chunk = matchedAssignments.slice(i, i + batchSize)
        const results = await Promise.allSettled(
          chunk.map(({ userId, row }) => {
            const spreadsheetRewards = buildUserRewardsFromRow(row)
            if (spreadsheetRewards && spreadsheetRewards.length > 0) {
              return saveUserRewards({
                userId,
                periodId: selectedPeriodId,
                rootOrganizationId: rootOrganizationId,
                organizationId: selectedOrgId,
                rootPeriodId: selectedRootPeriodId,
                packageId: packageIdToAssign,
                rewards: spreadsheetRewards,
              })
            }

            return assignPackage({
              packageId: packageIdToAssign,
              rootOrganizationId: rootOrganizationId,
              organizationId: selectedOrgId,
              rootPeriodId: selectedRootPeriodId,
              periodId: selectedPeriodId,
              userId,
            })
          })
        )

        results.forEach((result, index) => {
          if (result.status === 'fulfilled') {
            successful += 1
          } else {
            failed += 1
            failedUserIds.push(chunk[index].userId)
          }
        })

        setProcessingStatus(
          `Creating employee rewards (${Math.min(i + chunk.length, totalAssignments)}/${totalAssignments})`
        )
      }

      setEmployeeRewardSummary({
        total: totalAssignments,
        successful,
        failed,
        failedUserIds,
      })

      setProcessingStatus(
        failed === 0 ? 'Employee rewards created successfully.' : 'Employee rewards created with partial failures.'
      )
    } finally {
      setIsCreatingEmployeeRewards(false)
    }
  }, [
    token,
    effectivePackageId,
    selectedPeriodId,
    selectedRootPeriodId,
    purcentAppState?.selectedOrganization?.id,
    rootOrganizationId,
    unresolvedEmployeeMatchCount,
    matchedEmployeeRows,
    assignPackage,
    saveUserRewards,
    activePackageRewards,
    rewardsCatalogById,
    categoryNameById,
    categoryOrderById,
  ])

  const handleRewardEdit = (id: string, field: keyof RewardData, value: string | number | boolean) => {
    setRewards((current) =>
      current.map((reward) => {
        const rewardId = reward.id || reward.tempId
        if (rewardId !== id) return reward
        return {
          ...reward,
          [field]: value,
        }
      })
    )
  }

  const handleCategoryRemove = (id: string) => {
    setCategories((current) => current.filter((category) => (category.id || category.tempId) !== id))
  }

  const handleRewardRemove = (id: string) => {
    setRewards((current) => current.filter((reward) => (reward.id || reward.tempId) !== id))
  }

  const handleSaveCategories = async () => {
    if (!token || !profile) return
    setIsSavingCategories(true)

    try {
      const rootOrgId = getRootOrganizationId(profile)
      if (!rootOrgId) {
        setProcessingStatus('Missing organization context for categories.')
        return
      }

      const dtos: CreateRewardCategoryDTO[] = categories.map((category) => ({
        rootOrganizationId: rootOrgId,
        organizationId: rootOrgId,
        name: category.name.trim(),
        displayOrder: category.displayOrder,
        isActive: true,
      }))

      const response = dtos.length > 0 ? await bulkCreateCategories(dtos) : { categories: [], count: 0 }
      const upsertedCategories = response.categories ?? []

      const upsertedByName = new Map<string, any>()
      upsertedCategories.forEach((category) => {
        const key = String(category.name ?? '')
          .trim()
          .toLowerCase()
        if (!key) return
        upsertedByName.set(key, category)
      })

      const nextCategories = categories.map((category) => {
        const upserted = upsertedByName.get(category.name.trim().toLowerCase())
        if (!upserted) {
          return {
            ...category,
            isNew: !category.id,
          }
        }
        return {
          ...category,
          id: upserted.id,
          tempId: undefined,
          name: upserted.name,
          displayOrder: upserted.displayOrder ?? category.displayOrder,
          isNew: false,
        }
      })

      const categoriesById = new Map<string, CategoryData>()
      const categoriesByName = new Map<string, CategoryData>()
      nextCategories.forEach((category) => {
        if (category.id) categoriesById.set(category.id, category)
        categoriesByName.set(category.name.toLowerCase(), category)
      })

      const normalizedRewards = rewards.map((reward) => {
        const rawCategoryId = reward.categoryId.trim()
        const resolvedCategory = resolveCategoryReference(
          rawCategoryId,
          reward.categoryName,
          categoriesById,
          categoriesByName,
          nextCategories
        )
        return {
          ...reward,
          categoryId: resolvedCategory?.id || rawCategoryId,
          categoryName: resolvedCategory?.name || reward.categoryName,
        }
      })

      invalidateRewardsCache(rootOrgId, token)
      setCategories(nextCategories)
      setApprovedCategories(nextCategories)
      setRewards(normalizedRewards)

      const categorySummary = nextCategories
        .map((category) => (category.id ? `${category.name} (${category.id})` : category.name))
        .join(', ')

      if (selectedOption === 'employee-reward-package' || selectedOption === 'reward-package') {
        await sendAgentInstruction(
          `Categories approved and finalized: ${categorySummary}. Treat these category IDs/names as canonical for all next steps.`,
          false
        )
      }

      if (normalizedRewards.length > 0) {
        setPhase('review-rewards')
        setProcessingStatus('Rewards draft ready')
        return
      }

      setPhase('processing')
      setProcessingStatus('Categories approved. Preparing rewards...')
      await sendAgentInstruction(
        `Categories approved and created: ${categorySummary}. Please prepare the rewards draft using these category IDs.`,
        false
      )
    } catch (error: any) {
      const message = error?.message || 'Failed to save categories'
      setProcessingStatus(message)
    } finally {
      setIsSavingCategories(false)
    }
  }

  const handleSaveRewards = async () => {
    if (!token || !profile) return
    setIsSavingRewards(true)

    try {
      const rootOrgId = getRootOrganizationId(profile)
      if (!rootOrgId) {
        setProcessingStatus('Missing organization context for rewards.')
        return
      }

      if (hasUnresolvedRewardCategories) {
        setProcessingStatus('Waiting for category IDs to finish syncing. Please try again in a moment.')
        return
      }

      const rewardPayload: CreateRewardDTO[] = rewards.map((reward) => ({
        rootOrganizationId: rootOrgId,
        organizationId: rootOrgId,
        name: reward.name.trim(),
        categoryId: reward.categoryId,
        displayOrder: reward.displayOrder,
        supportsEmployeeContribution: reward.supportsEmployeeContribution,
      }))

      await bulkCreateRewards(rewardPayload)
      invalidateRewardsCache(rootOrgId, token)
      setApprovedRewards(rewards)

      if (selectedOption === 'employee-reward-package' || selectedOption === 'reward-package') {
        const rewardSummary = rewards.map((reward) => `${reward.name} [category_id:${reward.categoryId}]`).join('; ')
        await sendAgentInstruction(
          `Rewards approved and finalized: ${rewardSummary}. Treat these rewards and category mappings as canonical final state.`,
          false
        )
      }

      if (selectedOption === 'reward-package' || selectedOption === 'employee-reward-package') {
        const fallbackName = uploadedFile?.name?.replace(/\.[^/.]+$/, '') || ''
        setPackageName((current) => (current.trim().length > 0 ? current : fallbackName))
        setPackageDescription('')
        setPackageDisclaimer('')
        setPackageError(null)
        setCreatedPackageId(null)
        setPackageStep(0)
        setPackageRewardItems(new Map())
        setIsPackageInitialized(false)
        setPhase('review-package')
        setProcessingStatus(null)
      } else {
        setPhase('processing')
        setProcessingStatus('Rewards approved. Awaiting next step...')
      }
    } catch (error: any) {
      const message = error?.message || 'Failed to save rewards'
      setProcessingStatus(message)
    } finally {
      setIsSavingRewards(false)
    }
  }

  const handleGoToRewards = () => {
    navigate('/rewards/manage')
  }

  const handleViewEmployeeRewards = () => {
    const params = new URLSearchParams()
    if (selectedPeriodId) {
      params.set('periodId', selectedPeriodId)
    }
    if (effectivePackageId) {
      params.set('packageId', effectivePackageId)
    }
    const query = params.toString()
    navigate(query ? `/rewards/assign?${query}` : '/rewards/assign')
  }

  const handleBackToEmployeeMatching = () => {
    setEmployeeRewardSummary(null)
    setEmployeeMatchError(null)
  }

  const contentMaxWidth = 960

  const sanitizeAgentResponse = (text: string) => {
    let cleaned = text.replace(/```json[\s\S]*?```/gi, '').trim()
    cleaned = cleaned.replace(/MATCHED_PACKAGE_IDS?:\s*\[[^\]]*\]/gi, '')
    cleaned = cleaned.replace(
      /RECOMMENDED_PACKAGE_ID:\s*(none|[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12})/gi,
      ''
    )
    cleaned = cleaned.replace(/\(\s*ID:\s*`?[0-9a-fA-F-]{36}`?\s*\)/gi, '')
    if (cleaned.startsWith('{') && cleaned.endsWith('}')) {
      return ''
    }
    cleaned = cleaned.replace(/[ \t]{2,}/g, ' ')
    cleaned = cleaned.replace(/\n{3,}/g, '\n\n').trim()
    return cleaned
  }

  // Animation variants
  const containerVariants = {
    hidden: { opacity: 0, y: 16 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.45, ease: [0.25, 0.46, 0.45, 0.94] as const },
    },
    exit: {
      opacity: 0,
      y: -16,
      transition: { duration: 0.3, ease: [0.25, 0.46, 0.45, 0.94] as const },
    },
  }

  return (
    <Box sx={{ pb: 10 }}>
      <SimpleTitle title="Smart Import" />
      <Box
        sx={{
          minHeight: 'calc(100vh - 200px)',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          pb: 15,
        }}
      >
        <Box
          sx={{
            width: '100%',
            maxWidth: contentMaxWidth,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
          }}
        >
          <AnimatePresence mode="wait">
            {phase === 'welcome' && (
              <motion.div
                key="welcome"
                variants={containerVariants}
                initial="hidden"
                animate="visible"
                exit="exit"
                style={{ width: '100%', maxWidth: 560 }}
              >
                <Stack spacing={4} alignItems="center">
                  {/* Greeting */}
                  <Stack spacing={1.5} alignItems="center" textAlign="center">
                    <Typography variant="body1" color="text.secondary" sx={{ maxWidth: 400, lineHeight: 1.6 }}>
                      Drop a file and I'll help you create categories and rewards automatically.
                    </Typography>
                  </Stack>

                  {/* Drop zone */}
                  <SmartImportDropZone
                    isDragging={isDragging}
                    onDragChange={setIsDragging}
                    onFileSelect={handleFileSelect}
                    fileInputRef={fileInputRef}
                  />
                </Stack>
              </motion.div>
            )}

            {phase === 'select-type' && uploadedFile && (
              <motion.div
                key="select-type"
                variants={containerVariants}
                initial="hidden"
                animate="visible"
                exit="exit"
                style={{ width: '100%', maxWidth: 640 }}
              >
                <SmartImportOptions
                  file={uploadedFile}
                  options={importOptions}
                  selectedOption={selectedOption}
                  onSelect={handleSelectOption}
                  onBack={handleBackToUpload}
                  isLoading={isUploadingFile}
                />
              </motion.div>
            )}

            {phase === 'select-period' && uploadedFile && selectedOption === 'employee-reward-package' && (
              <motion.div
                key="select-period"
                variants={containerVariants}
                initial="hidden"
                animate="visible"
                exit="exit"
                style={{ width: '100%', maxWidth: 640 }}
              >
                <Stack spacing={3} alignItems="center">
                  <SmartImportFileInfo
                    fileName={uploadedFile.name}
                    importType={importOptions.find((o) => o.id === selectedOption)?.title || 'Import'}
                  />
                  <Stack spacing={1} sx={{ width: '100%' }}>
                    <Typography variant="h5" sx={{ fontWeight: 600 }}>
                      Select a period
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Employee reward package imports require a period context before processing.
                    </Typography>
                  </Stack>
                  <SinglePeriodSelector
                    periods={periodsResponse?.periods || []}
                    value={selectedPeriodId}
                    onChange={(periodId: string) => {
                      setSelectedPeriodId(periodId)
                      setSelectedRootPeriodId(periodId)
                    }}
                    label="Period"
                    placeholder={isLoadingPeriods ? 'Loading periods...' : 'Select a period'}
                    disabled={isLoadingPeriods}
                    disableClosed
                    rootPeriodOnly
                    pill
                    shrink
                    noTruncate
                  />
                  <Stack
                    direction={{ xs: 'column', sm: 'row' }}
                    spacing={2}
                    sx={{ width: '100%' }}
                    justifyContent="flex-end"
                  >
                    <Button variant="outlined" onClick={() => setPhase('select-type')}>
                      Back
                    </Button>
                    <Button
                      variant="contained"
                      onClick={() => handleStartProcessing('employee-reward-package')}
                      disabled={!selectedPeriodId || !selectedRootPeriodId || isLoadingPeriods || isUploadingFile}
                    >
                      Start import
                    </Button>
                  </Stack>
                </Stack>
              </motion.div>
            )}

            {phase === 'processing' && uploadedFile && (
              <motion.div
                key="processing"
                variants={containerVariants}
                initial="hidden"
                animate="visible"
                exit="exit"
                style={{ width: '100%', maxWidth: contentMaxWidth }}
              >
                <Stack spacing={3} alignItems="center">
                  <SmartImportProcessing
                    fileName={uploadedFile.name}
                    importType={importOptions.find((o) => o.id === selectedOption)?.title || 'Import'}
                    statusMessage={processingMessage}
                    title={processingTitle}
                  />
                  {messages.length > 0 && (
                    <motion.div
                      key="processing-messages"
                      variants={containerVariants}
                      initial="hidden"
                      animate="visible"
                      exit="exit"
                      style={{ width: '100%' }}
                    >
                      <ImportConversation
                        messages={messages}
                        scrollAnchorRef={scrollAnchorRef}
                        maxWidth={contentMaxWidth}
                        isThinking={isAgentThinking}
                        showThinking={false}
                        autoScroll={false}
                      />
                    </motion.div>
                  )}
                  {showNoChanges && (
                    <SmartImportNoChanges onUploadAnother={handleBackToUpload} onViewRewards={handleGoToRewards} />
                  )}
                  {(approvedCategories || approvedRewards) && (
                    <Stack spacing={2} sx={{ width: '100%' }}>
                      {approvedCategories && (
                        <ReviewTable
                          mode="categories"
                          categories={approvedCategories}
                          rewards={approvedRewards ?? rewards}
                          onCategoryEdit={handleCategoryEdit}
                          onRewardEdit={handleRewardEdit}
                          onCategoryRemove={handleCategoryRemove}
                          onRewardRemove={handleRewardRemove}
                          onCategoryReorder={handleCategoryReorder}
                          onRewardsReorder={handleRewardsReorder}
                          maxWidth={contentMaxWidth}
                          variant="approved"
                        />
                      )}
                      {approvedRewards && (
                        <ReviewTable
                          mode="rewards"
                          categories={approvedCategories ?? categories}
                          rewards={approvedRewards}
                          onCategoryEdit={handleCategoryEdit}
                          onRewardEdit={handleRewardEdit}
                          onCategoryRemove={handleCategoryRemove}
                          onRewardRemove={handleRewardRemove}
                          onCategoryReorder={handleCategoryReorder}
                          onRewardsReorder={handleRewardsReorder}
                          maxWidth={contentMaxWidth}
                          variant="approved"
                        />
                      )}
                    </Stack>
                  )}
                  {approvedRewards && !isAgentThinking && (
                    <SmartImportSummary
                      categoriesCount={(approvedCategories ?? categories).length}
                      rewardsCount={approvedRewards.length}
                      onViewRewards={handleGoToRewards}
                    />
                  )}
                  {isAgentThinking && (
                    <Box sx={{ width: '100%' }}>
                      <ThinkingIndicator />
                    </Box>
                  )}
                  <Box ref={pageScrollAnchorRef} />
                </Stack>
              </motion.div>
            )}

            {phase === 'review-categories' && uploadedFile && (
              <motion.div
                key="review-categories"
                variants={containerVariants}
                initial="hidden"
                animate="visible"
                exit="exit"
                style={{ width: '100%', maxWidth: contentMaxWidth }}
              >
                <Stack spacing={2} alignItems="center">
                  <SmartImportFileInfo
                    fileName={uploadedFile.name}
                    importType={importOptions.find((o) => o.id === selectedOption)?.title || 'Import'}
                  />
                  <motion.div
                    key="review-categories-conversation"
                    variants={containerVariants}
                    initial="hidden"
                    animate="visible"
                    exit="exit"
                    style={{ width: '100%' }}
                  >
                    <ImportConversation
                      messages={messages}
                      scrollAnchorRef={scrollAnchorRef}
                      isThinking={isAgentThinking}
                      showThinking={false}
                      autoScroll={false}
                    />
                  </motion.div>
                  <motion.div
                    key="review-categories-table"
                    variants={containerVariants}
                    initial="hidden"
                    animate="visible"
                    exit="exit"
                    style={{ width: '100%' }}
                  >
                    <ReviewTable
                      mode="categories"
                      categories={categories}
                      rewards={rewards}
                      onCategoryEdit={handleCategoryEdit}
                      onRewardEdit={handleRewardEdit}
                      onCategoryRemove={handleCategoryRemove}
                      onRewardRemove={handleRewardRemove}
                      onCategoryReorder={handleCategoryReorder}
                      onRewardsReorder={handleRewardsReorder}
                      maxWidth={contentMaxWidth}
                      footer={
                        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} justifyContent="flex-end">
                          <Button variant="outlined" onClick={() => setPhase('processing')}>
                            Keep chatting
                          </Button>
                          <Button variant="contained" onClick={handleSaveCategories} disabled={isSavingCategoryChanges}>
                            {isSavingCategoryChanges ? (
                              <CircularProgress size={20} color="inherit" />
                            ) : (
                              'Approve categories'
                            )}
                          </Button>
                        </Stack>
                      }
                    />
                  </motion.div>
                  {isAgentThinking && (
                    <Box sx={{ width: '100%' }}>
                      <ThinkingIndicator />
                    </Box>
                  )}
                  <Box ref={pageScrollAnchorRef} />
                </Stack>
              </motion.div>
            )}

            {phase === 'review-rewards' && uploadedFile && (
              <motion.div
                key="review-rewards"
                variants={containerVariants}
                initial="hidden"
                animate="visible"
                exit="exit"
                style={{ width: '100%', maxWidth: contentMaxWidth }}
              >
                <Stack spacing={2} alignItems="center">
                  <SmartImportFileInfo
                    fileName={uploadedFile.name}
                    importType={importOptions.find((o) => o.id === selectedOption)?.title || 'Import'}
                  />
                  <motion.div
                    key="review-rewards-conversation"
                    variants={containerVariants}
                    initial="hidden"
                    animate="visible"
                    exit="exit"
                    style={{ width: '100%' }}
                  >
                    <ImportConversation
                      messages={messages}
                      scrollAnchorRef={scrollAnchorRef}
                      isThinking={isAgentThinking}
                      showThinking={false}
                      autoScroll={false}
                    />
                  </motion.div>
                  <motion.div
                    key="review-rewards-table"
                    variants={containerVariants}
                    initial="hidden"
                    animate="visible"
                    exit="exit"
                    style={{ width: '100%' }}
                  >
                    <ReviewTable
                      mode="rewards"
                      categories={categories}
                      rewards={rewards}
                      onCategoryEdit={handleCategoryEdit}
                      onRewardEdit={handleRewardEdit}
                      onCategoryRemove={handleCategoryRemove}
                      onRewardRemove={handleRewardRemove}
                      onCategoryReorder={handleCategoryReorder}
                      onRewardsReorder={handleRewardsReorder}
                      maxWidth={contentMaxWidth}
                      footer={
                        <Stack spacing={1.25} sx={{ width: '100%' }}>
                          {hasUnresolvedRewardCategories && (
                            <Typography variant="body2" color="text.secondary">
                              Waiting for category IDs to sync ({unresolvedRewardCategoryCount} reward
                              {unresolvedRewardCategoryCount === 1 ? '' : 's'}). The approve button will enable
                              automatically.
                            </Typography>
                          )}
                          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} justifyContent="flex-end">
                            <Button variant="outlined" onClick={() => setPhase('processing')}>
                              Keep chatting
                            </Button>
                            <Button
                              variant="contained"
                              onClick={handleSaveRewards}
                              disabled={isSavingRewardChanges || hasUnresolvedRewardCategories}
                            >
                              {isSavingRewardChanges ? (
                                <CircularProgress size={20} color="inherit" />
                              ) : (
                                'Approve rewards'
                              )}
                            </Button>
                          </Stack>
                        </Stack>
                      }
                    />
                  </motion.div>
                  {isAgentThinking && (
                    <Box sx={{ width: '100%' }}>
                      <ThinkingIndicator />
                    </Box>
                  )}
                  <Box ref={pageScrollAnchorRef} />
                </Stack>
              </motion.div>
            )}

            {phase === 'review-package' && uploadedFile && (
              <motion.div
                key="review-package"
                variants={containerVariants}
                initial="hidden"
                animate="visible"
                exit="exit"
                style={{ width: '100%', maxWidth: contentMaxWidth }}
              >
                <Stack spacing={2} alignItems="center">
                  <SmartImportFileInfo
                    fileName={uploadedFile.name}
                    importType={importOptions.find((o) => o.id === selectedOption)?.title || 'Import'}
                  />
                  {messages.length > 0 && (
                    <motion.div
                      key="review-package-conversation"
                      variants={containerVariants}
                      initial="hidden"
                      animate="visible"
                      exit="exit"
                      style={{ width: '100%' }}
                    >
                      <ImportConversation
                        messages={messages}
                        scrollAnchorRef={scrollAnchorRef}
                        isThinking={isAgentThinking}
                        showThinking={false}
                        autoScroll={false}
                      />
                    </motion.div>
                  )}
                  <motion.div
                    key="review-package-form"
                    variants={containerVariants}
                    initial="hidden"
                    animate="visible"
                    exit="exit"
                    style={{ width: '100%' }}
                  >
                    <SmartImportPackageForm
                      activeStep={packageStep}
                      name={packageName}
                      onNameChange={setPackageName}
                      description={packageDescription}
                      disclaimer={packageDisclaimer}
                      onDescriptionChange={setPackageDescription}
                      onDisclaimerChange={setPackageDisclaimer}
                      rewardsByCategory={packageRewardsByCategory}
                      selectedRewardsByCategory={packageSelectedRewardsByCategory}
                      selectedRewardsCount={packageSelectedRewards.length}
                      totals={packageTotals}
                      allSelected={packageAllSelected}
                      someSelected={packageSomeSelected}
                      missingRewardCount={missingPackageRewardsCount}
                      isSubmitting={isCreatingPackage}
                      isNameValid={packageName.trim().length > 0}
                      canSubmit={canCreatePackage}
                      errorMessage={packageError}
                      onToggleRewardSelect={handlePackageToggleReward}
                      onContributionChange={handlePackageContributionChange}
                      onReorderRewards={handlePackageReorderRewards}
                      onToggleAll={handlePackageToggleAll}
                      onNext={handlePackageNext}
                      onBack={handlePackageBack}
                      onCancel={() => {
                        setPackageStep(0)
                        setPackageError(null)
                        setPhase('review-rewards')
                      }}
                      onCreate={handleCreatePackage}
                    />
                  </motion.div>
                  <Box ref={pageScrollAnchorRef} />
                </Stack>
              </motion.div>
            )}

            {phase === 'review-employee-matches' && uploadedFile && (
              <motion.div
                key="review-employee-matches"
                variants={containerVariants}
                initial="hidden"
                animate="visible"
                exit="exit"
                style={{ width: '100%', maxWidth: contentMaxWidth }}
              >
                <Stack spacing={2} alignItems="center">
                  <SmartImportFileInfo
                    fileName={uploadedFile.name}
                    importType={importOptions.find((o) => o.id === selectedOption)?.title || 'Import'}
                  />
                  {messages.length > 0 && (
                    <motion.div
                      key="review-employee-matches-conversation"
                      variants={containerVariants}
                      initial="hidden"
                      animate="visible"
                      exit="exit"
                      style={{ width: '100%' }}
                    >
                      <ImportConversation
                        messages={messages}
                        scrollAnchorRef={scrollAnchorRef}
                        isThinking={isAgentThinking}
                        showThinking={false}
                        autoScroll={false}
                      />
                    </motion.div>
                  )}
                  <Box sx={{ width: '100%' }}>
                    {employeeRewardSummary ? (
                      <SmartImportEmployeeRewardsSummary
                        totalEmployees={employeeRewardSummary.total}
                        successfulEmployees={employeeRewardSummary.successful}
                        failedEmployees={employeeRewardSummary.failed}
                        skippedRows={skippedEmployeeMatchCount}
                        periodLabel={selectedPeriodLabel}
                        onViewEmployeeRewards={handleViewEmployeeRewards}
                        onBackToMatching={handleBackToEmployeeMatching}
                      />
                    ) : (
                      <SmartImportEmployeeMatchTable
                        rows={employeeMatches}
                        userOptions={employeeMatchUserOptions}
                        onAssignUser={handleAssignEmployeeMatchUser}
                        onSkipRow={handleSkipEmployeeMatchRow}
                        footer={
                          <Stack spacing={2} sx={{ width: '100%' }}>
                            <Typography variant="body2" color="text.secondary">
                              Matched: {matchedEmployeeRows.length} | Unresolved: {unresolvedEmployeeMatchCount} |
                              Skipped: {skippedEmployeeMatchCount}
                            </Typography>

                            {!createdPackageId && (
                              <>
                                <Typography variant="body2" color="text.secondary">
                                  {matchedPackageCount === 0
                                    ? 'No matching existing package found. Create a new package to continue.'
                                    : matchedPackageCount === 1
                                      ? '1 matching package found. You can use it or create a new package.'
                                      : `${matchedPackageCount} matching packages found. Select one or create a new package.`}
                                </Typography>

                                {matchedPackageCount > 0 && (
                                  <Autocomplete
                                    options={matchedExistingPackageOptions}
                                    value={selectedExistingPackageOption}
                                    onChange={(_, newValue) => {
                                      setSelectedExistingPackageId(newValue?.id || '')
                                      setEmployeeMatchError(null)
                                    }}
                                    isOptionEqualToValue={(option, value) => option.id === value.id}
                                    getOptionLabel={(option) => option.name}
                                    renderInput={(params) => (
                                      <TextField
                                        {...params}
                                        label={matchedPackageCount === 1 ? 'Matching package' : 'Matching packages'}
                                        placeholder="Select a package"
                                        variant="outlined"
                                        shrink
                                        pill
                                      />
                                    )}
                                    sx={{ width: '100%' }}
                                  />
                                )}
                              </>
                            )}

                            {createdPackageId && (
                              <Typography variant="body2" color="text.secondary" sx={{ width: '100%' }}>
                                Using your newly created package for employee reward assignments.
                              </Typography>
                            )}

                            <Stack
                              direction={{ xs: 'column', sm: 'row' }}
                              spacing={2}
                              sx={{ width: '100%' }}
                              justifyContent="space-between"
                              alignItems={{ xs: 'stretch', sm: 'center' }}
                            >
                              {!createdPackageId && (
                                <Button
                                  variant="outlined"
                                  onClick={handleCreateNewPackageInstead}
                                  disabled={isAgentThinking || isCreatingEmployeeRewards}
                                >
                                  Create new package instead
                                </Button>
                              )}
                              <Button
                                variant="contained"
                                onClick={handleCreateEmployeeRewards}
                                disabled={
                                  isCreatingEmployeeRewards ||
                                  !effectivePackageId ||
                                  unresolvedEmployeeMatchCount > 0 ||
                                  matchedEmployeeRows.length === 0
                                }
                              >
                                {isCreatingEmployeeRewards ? (
                                  <CircularProgress size={20} color="inherit" />
                                ) : (
                                  'Create employee rewards'
                                )}
                              </Button>
                            </Stack>

                            {!effectivePackageId && !createdPackageId && (
                              <Typography variant="body2" color="text.secondary" sx={{ width: '100%' }}>
                                {matchedPackageCount > 0
                                  ? 'Select a matching package or create a new package to enable employee reward creation.'
                                  : 'Create a new package to enable employee reward creation.'}
                              </Typography>
                            )}

                            {employeeMatchError && (
                              <Typography variant="body2" color="error.main" sx={{ width: '100%' }}>
                                {employeeMatchError}
                              </Typography>
                            )}
                          </Stack>
                        }
                      />
                    )}
                  </Box>
                  {isAgentThinking && !employeeRewardSummary && (
                    <Box sx={{ width: '100%' }}>
                      <ThinkingIndicator />
                    </Box>
                  )}
                  <Box ref={pageScrollAnchorRef} />
                </Stack>
              </motion.div>
            )}
          </AnimatePresence>
        </Box>
      </Box>

      {/* Hidden file input */}
      <input
        ref={fileInputRef}
        type="file"
        accept=".csv,.xlsx,.xls,.pdf"
        style={{ display: 'none' }}
        onChange={(e) => {
          const file = e.target.files?.[0]
          if (file) handleFileSelect(file)
        }}
      />

      <ButtonActionBar
        show={
          !!sessionId &&
          (phase === 'review-categories' ||
            phase === 'review-rewards' ||
            (phase === 'review-employee-matches' && !employeeRewardSummary))
        }
        maxWidth={contentMaxWidth}
        padding={0}
        elevation={0}
        containerSx={{
          paddingBottom: { xs: '76px', sm: '60px' },
          left: { xs: 0, lg: drawerOffset },
        }}
        sx={{ backgroundColor: 'transparent', border: 'none', boxShadow: 'none', borderRadius: 0 }}
      >
        <SmartImportChatInput
          value={chatMessage}
          onChange={setChatMessage}
          onSubmit={handleSendMessage}
          isLoading={isAgentThinking}
        />
      </ButtonActionBar>
    </Box>
  )
}

export default SmartImport
